import React, { FC, ReactNode, useEffect, useState } from 'react';
import { Box, Button, Grid, makeStyles, Theme, fade, useMediaQuery } from '@material-ui/core';
import { GoogleMapComponent } from 'components/structures/main';
import { CurrentLocationCoordinates, CurrentLocation, CategoryType, INITIAL_PROJECTS_ADDRESSES } from 'config/mapSettings';
import {
  ArrowDownIcon,
  ConstructionIcon,
  EnergyIcon,
  EngineeringIcon,
  TechnologyIcon,
  WaterIcon,
  WaterWhiteIcon,
  WirelessIcon,
} from 'svgs/projects';
import clsx from 'clsx';

const useStyles = makeStyles((theme: Theme) => {
  return {
    gridItem: {
      paddingLeft: 12,
      borderTop: `1px solid ${theme.palette.secondary.light}`,
      cursor: 'pointer',
      [theme.breakpoints.up('md')]: {
        paddingLeft: 45,
      },
      [theme.breakpoints.up('xl')]: {
        paddingLeft: 205,
      },
    },
    item: {
      padding: '10px 0',
    },
    icon: {
      '& svg': {
        transform: 'scale(0.75)',
        '& path': {
          fill: '#9D9D9D',
        },
      },
    },
    arrowIcon: {
      '& svg': {
        '& path': {
          fill: '#9D9D9D',
        },
      },
    },
    activeIcon: {
      '& svg': {
        '& path': {
          fill: theme.palette.primary.main,
        },
      },
    },
  };
});

const INITIAL_CENTER_COORDINATES: CurrentLocationCoordinates = { lat: 41, lng: -97.0007065 };
const INITIAL_ZOOM_VALUE = 4.6;

const ProjectsMapSection: FC = () => {
  const [currentLocationList, setCurrentLocationList] = useState<Array<CurrentLocation>>(INITIAL_PROJECTS_ADDRESSES);
  const [centerMapCoordinates, setCenterMapCoordinates] = useState<CurrentLocationCoordinates>(INITIAL_CENTER_COORDINATES);
  const [mapZoom, setMapZoom] = useState<number>(INITIAL_ZOOM_VALUE);
  const [mapCategory, setMapCategory] = useState<keyof typeof CategoryType | 'all'>('all');
  const classes = useStyles();
  const settingsButton: Array<{ category: keyof typeof CategoryType; text: string; icon: ReactNode }> = [
    { category: 'engineering', text: 'Engineering', icon: <EngineeringIcon width="100%" viewBox="0 0 48 46" /> },
    { category: 'wireless', text: 'Wireless', icon: <WirelessIcon width="100%" viewBox="0 0 46 44" /> },
    { category: 'renewableenergy', text: 'Renewable Energy', icon: <EnergyIcon width="100%" viewBox="0 0 48 46" /> },
    { category: 'construction', text: 'Construction', icon: <ConstructionIcon width="100%" viewBox="0 0 53 34" /> },
    {
      category: 'technologydeployment',
      text: 'Technology Deployment',
      icon: <TechnologyIcon width="100%" viewBox="0 0 44 44" />,
    },
    { category: 'waterutilities', text: 'Water Utilities', icon: <WaterWhiteIcon width="100%" viewBox="0 0 44 44" /> },
  ];

  const [selectedCategory, setSelectedCategory] = useState(mapCategory);
  const [isCategoryOpen, setIsCategoryOpen] = useState(false);
  const [selectedCategoryOnMob, setSelectedCategoryOnMob] = useState<{ text: string; icon: ReactNode } | null>(null);
  const isMediumUp = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'));

  const getInitialMapSettings = (
    category: keyof typeof CategoryType | 'all',
  ): { coordinates: CurrentLocationCoordinates; zoom: number } => {
    switch (category) {
      case 'engineering':
        return { coordinates: { lat: 42.6211955, lng: -95.6824346 }, zoom: isMediumUp ? 3 : 2 };
      case 'wireless':
        return { coordinates: { lat: 53, lng: -120.629799 }, zoom: isMediumUp ? 3 : 3 };
      case 'renewableenergy':
        return { coordinates: { lat: 41.203323, lng: -87.194527 }, zoom: isMediumUp ? 5 : 4 };
      case 'construction':
        return { coordinates: { lat: 40.2, lng: -99.0007065 }, zoom: isMediumUp ? 3 : 2 };
      case 'technologydeployment':
        return { coordinates: { lat: 38.627003, lng: -90.199402 }, zoom: isMediumUp ? 5 : 4 };
      case 'waterutilities':
        return { coordinates: { lat: 42.6211955, lng: -95.6824346 }, zoom: isMediumUp ? 5 : 4 };
      default:
        return { coordinates: { lat: 41, lng: -97.0007065 }, zoom: 4.6 };
    }
  };
  const selectCategory = (category: keyof typeof CategoryType | 'all') => {
    setMapCategory(category);
    if (category === 'all') {
      setCurrentLocationList(INITIAL_PROJECTS_ADDRESSES);
      setCenterMapCoordinates(INITIAL_CENTER_COORDINATES);
      setMapZoom(INITIAL_ZOOM_VALUE);
    } else {
      setCurrentLocationList(INITIAL_PROJECTS_ADDRESSES.filter((location) => location.type === category));
    }
    const { coordinates, zoom } = getInitialMapSettings(category);
    setCenterMapCoordinates(coordinates);
    setMapZoom(zoom);
  };

  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.replace('#', '');
      selectCategory(hash as any);
      setMapCategory(hash as any);
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);

  const activeCategory = (category) => {
    selectCategory(category);
    window.history.pushState(null, '', window.location.pathname + window.location.search);
  };

  const handleCategoryClick = (category) => {
    setSelectedCategory(category);
    setIsCategoryOpen((prev) => prev !== category);
    activeCategory(category); // Call the function to handle the active category
  };

  const findCategory = () => {
    if (mapCategory === 'all') {
      setSelectedCategoryOnMob({ text: 'ALL LOCATION', icon: '' });
      return;
    }

    const categoryItem = settingsButton.find((item) => item.category === mapCategory);

    if (categoryItem) {
      setSelectedCategoryOnMob({ text: categoryItem.text, icon: categoryItem.icon });
    } else {
      setSelectedCategoryOnMob({ text: 'Engineering', icon: <EngineeringIcon width="100%" viewBox="0 0 48 46" /> });
    }
  };

  useEffect(() => {
    findCategory();
  }, [mapCategory]);

  return (
    <Box data-aos="fade-up">
      <Grid container direction="row" justify="space-between" spacing={0}>
        <Grid className={classes.gridItem} item md={4} xs={12}>
          {isMediumUp ? (
            settingsButton.map(({ text, category, icon }, index) => (
              <Box className={classes.item} key={index}>
                <Box pt={index === 0 ? 4 : 0} onClick={() => activeCategory(category)}>
                  <Box display="flex" justifyContent="start" alignItems={'center'}>
                    <Box maxWidth={55} className={`${classes.icon} ${category === mapCategory ? classes.activeIcon : ''}`}>
                      {icon}
                    </Box>
                    <Box
                      fontSize={20}
                      fontWeight={category === mapCategory ? 700 : 500}
                      color={category === mapCategory ? '#E0621E' : '#9299A6'}
                    >
                      {text.toUpperCase()}
                    </Box>
                  </Box>
                </Box>
              </Box>
            ))
          ) : (
            <>
              <Box className={classes.item}>
                <Box onClick={() => handleCategoryClick(mapCategory)}>
                  <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'}>
                    <Box display="flex" justifyContent="start" alignItems={'center'}>
                      <Box maxWidth={50} className={`${classes.icon} ${mapCategory ? classes.activeIcon : ''}`}>
                        {selectedCategoryOnMob?.icon}
                      </Box>
                      <Box fontSize={15} fontWeight={mapCategory ? 700 : 500} color={mapCategory ? '#E0621E' : '#9299A6'}>
                        {selectedCategoryOnMob?.text?.toUpperCase()}
                      </Box>
                    </Box>
                    <Box pr={3} className={`${classes.arrowIcon} ${mapCategory ? classes.activeIcon : ''}`}>
                      <ArrowDownIcon />
                    </Box>
                  </Box>
                </Box>
              </Box>
              {selectedCategory === mapCategory && isCategoryOpen && (
                <>
                  {settingsButton.map(({ text, category, icon }, index) => {
                    return (
                      mapCategory !== category && (
                        <Grid className={classes.item} item xs key={index}>
                          <Box pt={0} onClick={() => activeCategory(category)}>
                            <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'}>
                              <Box display="flex" justifyContent="start" alignItems={'center'}>
                                <Box
                                  maxWidth={50}
                                  className={`${classes.icon} ${category === mapCategory ? classes.activeIcon : ''}`}
                                >
                                  {icon}
                                </Box>
                                <Box
                                  fontSize={15}
                                  fontWeight={category === mapCategory ? 700 : 500}
                                  color={category === mapCategory ? '#E0621E' : '#9299A6'}
                                >
                                  {text.toUpperCase()}
                                </Box>
                              </Box>
                            </Box>
                          </Box>
                        </Grid>
                      )
                    );
                  })}
                  <Box
                    border={'1px solid #E0621E'}
                    width={189}
                    height={56}
                    display={'flex'}
                    alignItems={'center'}
                    justifyContent={'center'}
                    onClick={() => selectCategory('all')}
                    color={'#E0621E'}
                    fontWeight={500}
                    fontSize={17}
                    mt={6}
                    mb={8}
                  >
                    ALL LOCATIONS
                  </Box>
                </>
              )}
            </>
          )}
          {isMediumUp && (
            <Box
              border={'1px solid #E0621E'}
              width={189}
              height={56}
              display={'flex'}
              alignItems={'center'}
              justifyContent={'center'}
              onClick={() => selectCategory('all')}
              color={'#E0621E'}
              fontWeight={500}
              fontSize={17}
              mt={8}
            >
              ALL LOCATIONS
            </Box>
          )}
        </Grid>
        <Grid item md={8} xs={12}>
          <GoogleMapComponent locations={currentLocationList} zoom={mapZoom} centerCoordinates={centerMapCoordinates} />
        </Grid>
      </Grid>
    </Box>
  );
};

export default ProjectsMapSection;
